import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Landmark, ClipboardCopy, Check, X } from "lucide-react";
import { copyText, requestText } from "@/components/QuickRequest";
import { itDate } from "@/lib/pa";
import { MepaCompare, groupByBatch } from "@/components/MepaCompare";

const STATUS = { pending: ["In attesa di risposta", "chip-navy"], available: ["Disponibile", "chip-gold"], unavailable: ["Non disponibile", "inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full border border-red-300 text-red-700 bg-red-50"] };

function Row({ r, isProvider, onReply }) {
  const [note, setNote] = useState("");
  const [label, chip] = STATUS[r.status];
  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-2" data-testid={`mepa-request-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-navy">{isProvider ? r.clientName : r.operatorName}</div>
          <div className="text-xs text-slate-500">{r.eventType} · {itDate(r.eventDate)} · {r.eventLocation}</div>
        </div>
        <span className={chip} data-testid={`mepa-request-status-${r.id}`}>{label}</span>
      </div>
      {r.notes && <p className="text-xs text-slate-600">Note: {r.notes}</p>}
      {r.replyNote && <p className="text-xs text-slate-600 border-l-2 border-gold pl-2">Risposta: {r.replyNote}</p>}
      {isProvider && r.status === "pending" && (
        <div className="flex flex-wrap gap-2 items-center">
          <input data-testid={`mepa-reply-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Messaggio facoltativo per il committente" className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-1.5 text-xs"/>
          <button type="button" data-testid={`mepa-reply-yes-${r.id}`} onClick={() => onReply(r.id, true, note)} className="btn-pill-gold text-xs"><Check className="w-3.5 h-3.5"/>Sono disponibile</button>
          <button type="button" data-testid={`mepa-reply-no-${r.id}`} onClick={() => onReply(r.id, false, note)} className="btn-pill-outline text-xs"><X className="w-3.5 h-3.5"/>Non disponibile</button>
        </div>
      )}
      {!isProvider && <button type="button" data-testid={`mepa-request-copy-${r.id}`} onClick={() => copyText(requestText(r))} className="btn-pill-outline text-xs"><ClipboardCopy className="w-3.5 h-3.5"/>Copia dati Trattativa Diretta</button>}
    </div>
  );
}

export function MepaRequestsCard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const isProvider = ["artist", "supplier"].includes(user.role);
  const load = () => api.get("/mepa/requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const reply = async (id, available, note) => {
    try { await api.post(`/mepa/requests/${id}/reply`, { available, note: note.trim() || null }); toast.success("Risposta inviata al committente"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  if (!items || (isProvider && items.length === 0 && !user.mepaEnabled)) return null;
  return (
    <div className="mt-8 card-premium p-6 space-y-4" data-testid="mepa-requests-card">
      <div className="flex items-center gap-2"><Landmark className="w-5 h-5 text-gold"/><h3 className="font-display text-xl font-bold">{isProvider ? "Richieste di disponibilità MePA" : "Le tue richieste Trattativa Rapida"}</h3></div>
      {items.length === 0 && <p className="text-sm text-slate-500" data-testid="mepa-requests-empty">{isProvider ? "Nessuna richiesta ricevuta." : "Nessuna richiesta inviata: parti dalla Vetrina MePA in Home."}</p>}
      <div className="space-y-3">{isProvider ? items.map((r) => <Row key={r.id} r={r} isProvider onReply={reply}/>)
        : groupByBatch(items).map(([k, rows]) => rows.length > 1 ? <MepaCompare key={k} batchId={k} rows={rows}/> : <Row key={k} r={rows[0]} isProvider={false} onReply={reply}/>)}</div>
    </div>
  );
}
